import React, {
    FC, KeyboardEvent, useEffect, useRef, useState,
} from 'react';

import { Input, InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS } from '../constants';
import { CloseIcon, DownIcon, UpIcon } from './Icons';
import styles from '../PdfTileViewer.module.scss';

interface SearchBarProps {
    focusSignal: number;
    resultCount: number;
    active: number;
    isRunning: boolean;
    hasFailed: boolean;
    onSearch: (query: string) => void;
    onStep: (direction: 1 | -1) => void;
    onClose: () => void;
}

const SearchBar: FC<SearchBarProps> = ({
    focusSignal,
    resultCount,
    active,
    isRunning,
    hasFailed,
    onSearch,
    onStep,
    onClose,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState<string>('');
    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, [focusSignal]);

    useEffect(() => {
        const timer = window.setTimeout(() => onSearch(value), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [value]);

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            onStep(event.shiftKey ? -1 : 1);
        }
        if (event.key === 'Escape') {
            onClose();
        }
    };

    const getCountLabel = (): string => {
        if (value.length < SEARCH_MIN_CHARS) {
            return '';
        }
        if (hasFailed) {
            return t('viewer.tileViewer.searchFailed');
        }
        if (resultCount) {
            return `${active + 1} / ${resultCount}${isRunning ? '+' : ''}`;
        }
        return isRunning ? '…' : t('viewer.tileViewer.noResults');
    };

    return (
        <div className={styles['search-bar']}>
            <Input
                ref={inputRef}
                value={value}
                placeholder={t('viewer.tileViewer.searchPlaceholder')}
                aria-label={t('viewer.tileViewer.searchPlaceholder')}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="search"
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={onKeyDown}
            />
            <span className={styles['search-count']}>{getCountLabel()}</span>
            <button
                type="button"
                className={`${styles.tool} ${styles.small}`}
                title={t('viewer.tileViewer.previous')}
                aria-label={t('viewer.tileViewer.previous')}
                onClick={() => onStep(-1)}
            >
                <UpIcon />
            </button>
            <button
                type="button"
                className={`${styles.tool} ${styles.small}`}
                title={t('viewer.tileViewer.next')}
                aria-label={t('viewer.tileViewer.next')}
                onClick={() => onStep(1)}
            >
                <DownIcon />
            </button>
            <button
                type="button"
                className={`${styles.tool} ${styles.small}`}
                title={t('viewer.tileViewer.close')}
                aria-label={t('viewer.tileViewer.close')}
                onClick={onClose}
            >
                <CloseIcon />
            </button>
        </div>
    );
};

export default SearchBar;
